import { z } from "zod";

/**
 * The guest-details schema (tasks/diner-booking-ui.md tech decision: react-hook-form + zod, ADR-0027).
 * It encodes ONLY R4's "clearly malformed" floor — structural garbage the client blocks inline — and
 * NOTHING plausible: the server owns plausibility flagging (accept-and-flag via `contactFlagged`),
 * never the client. So `jean+test@mail.fr` and `06 12 34 56 78` submit untouched, while `jean@` and a
 * 4-digit phone are blocked before the round-trip. Server `ProblemDetail.fieldErrors` (ADR-0008) map
 * back onto these same fields.
 *
 * Names follow DOMAIN.md §B.3 `GuestName` (each side ≤40, ≥1 non-blank — here the given name is
 * required). The allergy note is its OWN field, never merged into special requests. The terms
 * acknowledgement is a hard gate (R3); the submit button is also disabled until it is checked.
 */

/** Structural e-mail floor: exactly one `@`, a dotted domain. Deliberately NOT a plausibility check. */
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Digit-count plausibility only (R4): a French national number is 10 digits, `+33…` 11 — 8..15 passes. */
export function phoneDigitsPlausible(raw: string): boolean {
  const digits = raw.replace(/\D/g, "");
  return digits.length >= 8 && digits.length <= 15;
}

export function emailStructural(raw: string): boolean {
  return EMAIL_RE.test(raw.trim());
}

type Translate = (key: string) => string;

export function guestDetailsSchema(t: Translate, opts: { requestVariant: boolean }) {
  return (
    z
      .object({
        givenName: z
          .string()
          .trim()
          .min(1, t("widget.booking.err.nameRequired"))
          .max(40, t("widget.booking.err.tooLong")),
        familyName: z.string().trim().max(40, t("widget.booking.err.tooLong")),
        // Phone AND email are individually optional — but the booking needs at least ONE (the cross-field
        // rule below, matching the backend's `a booking must carry a phone or an email` invariant). Each is
        // format-checked only WHEN provided (empty passes; the "at least one" rule owns emptiness).
        phone: z
          .string()
          .trim()
          .refine((v) => v === "" || phoneDigitsPlausible(v), {
            message: t("widget.booking.err.phoneInvalid"),
          }),
        email: z
          .string()
          .trim()
          .refine((v) => v === "" || EMAIL_RE.test(v), {
            message: t("widget.booking.err.emailInvalid"),
          }),
        occasion: z.string().trim().max(80, t("widget.booking.err.tooLong")),
        specialRequests: z.string().trim().max(500, t("widget.booking.err.tooLong")),
        allergyNote: z.string().trim().max(500, t("widget.booking.err.tooLong")),
        zonePreference: z.string(),
        // The large-party request path (SC-5) carries a provisional desired time; the instant path leaves
        // it blank (the live slot already fixes the time).
        preferredTime: opts.requestVariant
          ? z.string().regex(/^\d{2}:\d{2}$/, t("widget.booking.err.timeRequired"))
          : z.string(),
        termsAccepted: z.boolean().refine((v) => v === true, {
          message: t("widget.booking.err.termsRequired"),
        }),
      })
      // At least one contact so the restaurant can reach the guest (the domain invariant). Surfaced on the
      // email field when both are blank; a filled phone alone (or email alone) satisfies it.
      .refine((d) => d.phone.trim() !== "" || d.email.trim() !== "", {
        message: t("widget.booking.err.contactRequired"),
        path: ["email"],
      })
  );
}

export type GuestDetailsValues = z.infer<ReturnType<typeof guestDetailsSchema>>;

/** Empty-to-undefined for optional free-text fields when building the API payload. */
export function orUndefined(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}
